import React from 'react';
import {
    GET_CONNECTIONS_ENDPOINT,
    GET_INVITES_ENDPOINT,
    GET_USERS_ENDPOINT,
    InviteType,
    Referrer
} from '../../types/AnalyticsApi';
import { DataPoint, HandledData, TimeSeriesDataPoint } from '../../types/CompositeDataProvider';
import CompositeDataProvider from '../DataProviders/CompositeDataProvider';
import Legend from '../Legends/Legend';
import LineChartContainer from '../LineChart/LineChartContainer';
import ChartSectionContainer from '../Section/ChartSectionContainer';
import SectionChart from '../Section/SectionChart';
import SectionHeader from '../Section/SectionHeader';

interface DashboardThreeSeriesInvitesChartProps {
    countryCode?: string[] | null;
    from?: string | null;
    to?: string | null;
    companyId?: string[] | null;
    departmentId?: string[] | null;
}

const addToInterval = (intervals: Map<number, DataPoint[]>, date: number, key: string, value: number) => {
    if (!intervals.has(date)) {
        intervals.set(date, []);
    }

    intervals.get(date)?.push({
        key,
        value
    });
};

const dataMerger = (dataSets: HandledData[]) => {
    let mergedData: TimeSeriesDataPoint[] = [];
    let intervals = new Map<number, DataPoint[]>();

    for (let dataSet of dataSets) {
        if (!dataSet.data) {
            continue;
        }

        for (let data of dataSet.data) {
            addToInterval(intervals, new Date(Date.parse(data.key)).getTime(), dataSet.id, data.value);
        }
    }

    intervals.forEach((value, key) => {
        mergedData.push({
            key: new Date(key),
            value
        });
    });

    return mergedData;
};

const DashboardThreeSeriesInvitesChart: React.FC<DashboardThreeSeriesInvitesChartProps> = (props) => {
    return (
        <ChartSectionContainer>
            <SectionHeader>New User Email Invites Sent vs Onboarded vs Connected</SectionHeader>

            <Legend
                items={[
                    {
                        name: 'Sent',
                        color: '#d9a5ff'
                    },
                    {
                        name: 'Onboarded',
                        color: '#ffeda5'
                    },
                    {
                        name: 'Connected',
                        color: '#5cefff'
                    }
                ]}
            />

            <SectionChart>
                {/* Three series line chart */}
                <CompositeDataProvider
                    dataFormat="TIME_SERIES"
                    sources={[
                        {
                            id: 'Connected',
                            src: GET_CONNECTIONS_ENDPOINT,
                            options: {
                                connectionType: ['Accepted'],
                                isRelatedByInvite: true,
                                cardinality: true,
                                acceptedInviteWithinSeconds: 86400 // 24 hours
                            },
                            handler: (res: any) => res.data
                        },
                        {
                            id: 'Onboarded',
                            src: GET_USERS_ENDPOINT,
                            options: {
                                referrer: Referrer.Email,
                                acceptedInviteWithinSeconds: 86400 * 7 // 7 days
                            },
                            handler: (res: any) => res.data
                        },
                        {
                            id: 'Sent',
                            src: GET_INVITES_ENDPOINT,
                            options: {
                                isExistingUser: false,
                                type: InviteType.Email
                            },
                            handler: (res: any) => res.data
                        }
                    ]}
                    merger={dataMerger}
                    options={{
                        ...props
                    }}
                    updateOnEventStrings={['INVITE_SENT', 'USER_JOINED', 'CONNECTION_REQUEST_ACCEPTED']}
                    throttleMilliseconds={10000}
                >
                    <LineChartContainer
                        margin={{
                            top: 10,
                            right: 30,
                            left: 75,
                            bottom: 40
                        }}
                    />
                </CompositeDataProvider>
            </SectionChart>
        </ChartSectionContainer>
    );
};

export default DashboardThreeSeriesInvitesChart;
